<template>
  <div
    class="list-filter-wrapper"
    :id="`list-filter-${filterKey}`"
    :ref="`list-filter-${filterKey}`"
  >
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: "ListFilterWrapper",
  props: {
    filterKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      observer: null,
      mutationObserver: null,
    };
  },
  methods: {
    setupObserver() {
      // Clean up any existing observer before setting a new one
      this.removeObserver();

      const listFilterWrapper = this.$refs[`list-filter-${this.filterKey}`];

      if (!listFilterWrapper) return;

      // Select the last item of the refinement list
      const lastItemElement = listFilterWrapper.querySelector(
        `.endless-scroll-trigger:last-child`,
      );

      if (lastItemElement) {
        // Set up IntersectionObserver to observe the last item
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                listFilterWrapper.classList.add("last-item-visible");
              } else {
                listFilterWrapper.classList.remove("last-item-visible");
              }
            });
          },
          {
            root: null,
            threshold: 0.1,
          },
        );

        // Start observing the last item
        this.observer.observe(lastItemElement);
      }
    },
    removeObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },
    setupMutationObserver() {
      const listFilterWrapper = this.$refs[`list-filter-${this.filterKey}`];

      if (!listFilterWrapper) return;

      // MutationObserver to detect changes in the slot content
      this.mutationObserver = new MutationObserver(() => {
        this.removeObserver(); // Clean up the existing observer
        this.setupObserver(); // Re-setup the IntersectionObserver
      });

      // Observe changes in child nodes, subtree, and attributes of the slot content
      this.mutationObserver.observe(listFilterWrapper, {
        childList: true,
        subtree: true,
      });
    },
    removeMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    },
  },
  mounted() {
    this.setupObserver();
    this.setupMutationObserver();
  },
  updated() {
    // Re-setup the observers whenever the component updates (e.g., items change)
    this.removeObserver(); // Clean up the existing observer
    this.setupObserver();
  },
  beforeUnmount() {
    // Clean up the observers when the component is destroyed
    this.removeObserver();
    this.removeMutationObserver();
  },
};
</script>

<style lang="scss" scoped>
.list-filter-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 15px;
}

:deep(.ais-Highlight-highlighted) {
  background-color: var(--search-filter-accent-color);
}

:deep(.search-filter-filter-location-list) {
  margin-top: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ccc;
}
</style>
